
import React, { useState, useEffect, useMemo } from 'react'
import ProtectedRoute from '../../components/ProtectedRoute'
import { AuthProvider } from '@/lib/auth-context'
import { MetricCard } from '@/components/MetricCard'
import { EmptyState } from '@/components/ui/EmptyState'
import { SensitiveText } from '@/components/privacy/SensitiveText'
import {
  Calendar,
  Clock,
  CheckCircle,
  AlertTriangle,
  Plus,
  Calculator,
  FileText,
  DollarSign,
  Users,
  TrendingUp,
  Filter,
  Download
} from 'lucide-react'
import {
  validateVacationRequest
} from '../../lib/brazilian-labor'
import { getMyVacationBalance, getVacationBalance, listMyVacationRequests } from '@/lib/services/vacation.service'
import { formatDateBR } from '@/lib/utils/date-utils'
import type { VacationBalance as APIVacationBalance, VacationRequest as APIVacationRequest } from '@/types/vacation'
import { getMyAllReports } from '@/api/workforce'
import { useAuth } from '@/hooks/useAuth'
import { usePageTitle } from '@/stores/page-title-store'

// Vacation Alert Types
interface VacationAlert {
  id: string
  employeeId: string
  employeeName: string
  type: 'anniversary_approaching' | 'excessive_balance' | 'critical_balance'
  severity: 'warning' | 'critical'
  daysUntilAnniversary?: number
  currentBalance: number
  message: string
  actionRequired: boolean
}

// NOTE: Employee list features removed - now using only workforce API
// The vacation page only needs balance and requests from the workforce API
// No need for dashboard config or employee lists from the monumenta API


// Calculate vacation alerts for real employees
function calculateVacationAlerts(employees: Employee[]): VacationAlert[] {
  const alerts: VacationAlert[] = []
  const today = new Date()

  employees.forEach(employee => {
    const hireDate = new Date(employee.hireDate)
    const currentBalance = employee.vacationBalance.daysAvailable
    
    // Calculate next anniversary date
    const nextAnniversary = new Date(hireDate)
    nextAnniversary.setFullYear(today.getFullYear())
    
    // If anniversary has passed this year, get next year's anniversary
    if (nextAnniversary < today) {
      nextAnniversary.setFullYear(today.getFullYear() + 1)
    }
    
    // Calculate days until anniversary
    const daysUntilAnniversary = Math.ceil(
      (nextAnniversary.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
    )
    
    // Critical Alert: More than 30 days balance (didn't take vacation last period)
    if (currentBalance > 30) {
      alerts.push({
        id: `critical-${employee.id}`,
        employeeId: employee.id,
        employeeName: employee.name,
        type: 'critical_balance',
        severity: 'critical',
        currentBalance,
        message: `Colaborador com ${currentBalance} dias de saldo! Não gozou férias no último período aquisitivo.`,
        actionRequired: true
      })
    }
    // Warning Alert: 20-30 days balance
    else if (currentBalance > 20) {
      alerts.push({
        id: `excessive-${employee.id}`,
        employeeId: employee.id,
        employeeName: employee.name,
        type: 'excessive_balance',
        severity: 'warning',
        currentBalance,
        message: `Saldo elevado de ${currentBalance} dias. Considere agendar férias.`,
        actionRequired: false
      })
    }
    
    // Anniversary approaching with positive balance
    if (daysUntilAnniversary <= 90 && currentBalance > 0) {
      const severity = daysUntilAnniversary <= 30 ? 'critical' : 'warning'
      alerts.push({
        id: `anniversary-${employee.id}`,
        employeeId: employee.id,
        employeeName: employee.name,
        type: 'anniversary_approaching',
        severity,
        daysUntilAnniversary,
        currentBalance,
        message: `Aniversário de admissão em ${daysUntilAnniversary} dias com ${currentBalance} dias disponíveis.`,
        actionRequired: daysUntilAnniversary <= 30
      })
    }
  })
  
  // Dedup alerts by employeeId — keep only the highest-severity alert per employee
  const alertsByEmployee = new Map<string, VacationAlert>()
  for (const alert of alerts) {
    const existing = alertsByEmployee.get(alert.employeeId)
    if (!existing || alert.severity === 'critical' && existing.severity !== 'critical') {
      alertsByEmployee.set(alert.employeeId, alert)
    }
  }
  const dedupedAlerts = Array.from(alertsByEmployee.values())

  // Sort by severity and urgency
  return dedupedAlerts.sort((a, b) => {
    if (a.severity !== b.severity) {
      return a.severity === 'critical' ? -1 : 1
    }
    if (a.type === 'critical_balance') return -1
    if (b.type === 'critical_balance') return 1
    return (a.daysUntilAnniversary || 0) - (b.daysUntilAnniversary || 0)
  })
}

// Using VacationRequest from API types instead of local interface
// Type alias for compatibility with existing UI code
interface VacationRequest {
  id: string
  employeeId: string
  employeeName: string
  department: string
  startDate: string
  endDate: string
  days: number
  type: 'vacation' | 'sell' | 'advance'
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED'
  requestDate: string
  approvedBy?: string
  notes?: string
}

interface Employee {
  id: string
  name: string
  department: string
  hireDate: string
  position: string
  vacationBalance: APIVacationBalance // Using API type instead of VacationCalculation
}


// Removed: generateRealVacationRequests() - now using real API data

// Map API vacation request to UI format
function mapApiRequestToUI(apiRequest: APIVacationRequest, employeeName: string = 'Unknown', department: string = 'N/A'): VacationRequest {
  return {
    id: apiRequest.requestId,
    employeeId: apiRequest.employeeId,
    employeeName,
    department,
    startDate: apiRequest.startDate,
    endDate: apiRequest.endDate,
    days: apiRequest.totalDays,
    type: 'vacation', // Default type - can be enhanced later
    status: apiRequest.status,
    requestDate: apiRequest.requestedAt,
    approvedBy: apiRequest.approvedBy,
    notes: apiRequest.notes
  }
}

function VacationContent() {
  usePageTitle('Férias')
  const { user } = useAuth()
  const [selectedTab, setSelectedTab] = useState<'requests' | 'employees' | 'calculator' | 'reports'>('employees')
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null)
  const [showCalculator, setShowCalculator] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [realEmployees, setRealEmployees] = useState<Employee[]>([])
  const [vacationBalance, setVacationBalance] = useState<APIVacationBalance | null>(null)
  const [realRequests, setRealRequests] = useState<VacationRequest[]>([])

  // Load vacation data function (defined outside useEffect for retry capability)
  const loadVacationData = async () => {
      if (!user) {
        setError('User not authenticated')
        setIsLoading(false)
        return
      }

      try {
        setIsLoading(true)
        setError(null)

        // Admin has no employee record — skip personal balance/requests, go straight to team view
        if (user.employee) {
          // Fetch balance and requests independently — one failure shouldn't block the other
          try {
            const [balanceResp, requestsResp] = await Promise.all([
              getMyVacationBalance().catch((err: any) => {
                console.warn('[Vacation] Failed to load personal balance:', err?.message)
                return null
              }),
              listMyVacationRequests().catch((err: any) => {
                console.warn('[Vacation] Failed to load personal requests:', err?.message)
                return { items: [] }
              })
            ])

            console.log('[Vacation] Balance response:', balanceResp)
            console.log('[Vacation] Requests response:', requestsResp)

            // Backend returns single object, not array
            if (balanceResp && balanceResp.employeeId) {
              setVacationBalance(balanceResp as APIVacationBalance)
            }

            // Map API requests to UI format
            // Handle both 'items' and direct array responses
            const requestItems = requestsResp?.items || requestsResp || []
            const mappedRequests = requestItems.map((req: any) =>
              mapApiRequestToUI(req, user.employee?.personalInfo.name, `Department ${user.employee?.currentEngagement?.departmentId || ''}`)
            )
            setRealRequests(mappedRequests)
          } catch (personalError: any) {
            console.warn('[Vacation] Error loading personal vacation data (non-blocking):', personalError?.message)
          }
        }

        // Fetch all reports (including indirect reports) and their vacation balances from workforce API
        try {
          const allReportsResponse = await getMyAllReports({ limit: 1000 })
          // Filter out terminated employees — they should not appear in vacation alerts
          const directReports = allReportsResponse.items.filter(
            (member: any) => member.status?.toUpperCase() !== 'TERMINATED'
          )
          console.log('[Vacation] All reports received:', {
            count: directReports?.length || 0,
            data: directReports
          })

          // If no direct reports, set empty array and continue
          if (!directReports || directReports.length === 0) {
            console.log('[Vacation] No direct reports found - user may not be a manager')
            setRealEmployees([])
          } else {
            // Fetch vacation balance for each direct report
            const employeesWithBalances = await Promise.allSettled(
              directReports.map(async (member: any) => {
                console.log('[Vacation] Processing direct report:', {
                  employeeId: member.employeeId,
                  name: member.personalInfo?.name,
                  startDate: member.currentEngagement?.startDate,
                  department: member.currentEngagement?.departmentName,
                  role: member.currentEngagement?.roleName
                })

                try {
                  const memberBalance = await getVacationBalance(member.employeeId)
                  console.log(`[Vacation] Balance for ${member.employeeId}:`, memberBalance)

                  // Normalize balance data - API returns totalDaysEarned, UI expects daysEarned
                  const normalizedBalance: APIVacationBalance = {
                    ...memberBalance,
                    daysEarned: memberBalance.totalDaysEarned || memberBalance.daysEarned || 0,
                    acquisitionStartDate: memberBalance.acquisitionStart || memberBalance.acquisitionStartDate,
                    acquisitionEndDate: memberBalance.acquisitionEnd || memberBalance.acquisitionEndDate
                  }
                  console.log(`[Vacation] Normalized balance for ${member.employeeId}:`, {
                    original: memberBalance,
                    normalized: normalizedBalance
                  })

                  return {
                    id: member.employeeId,
                    name: member.personalInfo?.name || 'Unknown',
                    department: member.currentEngagement?.departmentName || 'Unknown',
                    hireDate: member.currentEngagement?.startDate || new Date().toISOString(),
                    position: member.currentEngagement?.roleName || 'Unknown',
                    vacationBalance: normalizedBalance
                  }
                } catch (balanceError: any) {
                  console.error(`[Vacation] Error fetching balance for ${member.employeeId}:`, {
                    error: balanceError,
                    message: balanceError?.message,
                    status: balanceError?.response?.status
                  })
                  // Return employee with empty balance data
                  // Create a minimal balance object that matches APIVacationBalance structure
                  const emptyBalance: APIVacationBalance = {
                    employeeId: member.employeeId,
                    tenantId: '',
                    periodYear: new Date().getFullYear(),
                    acquisitionStartDate: member.currentEngagement?.startDate || new Date().toISOString(),
                    acquisitionEndDate: new Date().toISOString(),
                    daysEarned: 0,
                    daysUsed: 0,
                    daysScheduled: 0,
                    daysAvailable: 0,
                    expirationDate: new Date().toISOString(),
                    daysAtRisk: 0,
                    isExpiringSoon: false
                  }

                  return {
                    id: member.employeeId,
                    name: member.personalInfo?.name || 'Unknown',
                    department: member.currentEngagement?.departmentName || 'Unknown',
                    hireDate: member.currentEngagement?.startDate || new Date().toISOString(),
                    position: member.currentEngagement?.roleName || 'Unknown',
                    vacationBalance: emptyBalance
                  }
                }
              })
            )

            const employees = employeesWithBalances
              .filter((result): result is PromiseFulfilledResult<Employee> => result.status === 'fulfilled')
              .map(result => result.value)

            console.log('[Vacation] Final employees with balances:', {
              count: employees.length,
              employees
            })
            setRealEmployees(employees)
          }
        } catch (teamError: any) {
          console.error('[Vacation] Error loading direct reports:', {
            error: teamError,
            message: teamError?.message,
            status: teamError?.response?.status,
            stack: teamError?.stack
          })
          // Continue without team list - just show user's own balance
          setRealEmployees([])
        }
      } catch (err) {
        console.error('Error loading vacation data:', err)
        setError(err instanceof Error ? err.message : 'Failed to load vacation data')
      } finally {
        setIsLoading(false)
      }
  }

  // Load data on component mount
  useEffect(() => {
    loadVacationData()
  }, [user])

  // Calculate vacation alerts based on loaded employees
  const vacationAlerts = useMemo(() => {
    return realEmployees.length > 0 ? calculateVacationAlerts(realEmployees) : []
  }, [realEmployees])

  // Removed: realRequests useMemo - now using state from API

  const stats = useMemo(() => ({
    totalRequests: realRequests.length,
    pendingRequests: realRequests.filter(req => req.status === 'PENDING').length,
    approvedRequests: realRequests.filter(req => req.status === 'APPROVED').length,
    totalDaysRequested: realRequests.reduce((sum, req) => sum + req.days, 0),
    criticalAlerts: vacationAlerts.filter(alert => alert.severity === 'critical').length,
    warningAlerts: vacationAlerts.filter(alert => alert.severity === 'warning').length
  }), [realRequests, vacationAlerts])

  const getStatusBadge = (status: string) => {
    const styles = {
      PENDING: 'status-pending',
      APPROVED: 'status-primary',
      REJECTED: 'status-inactive',
      CANCELLED: 'status-inactive'
    }
    return styles[status as keyof typeof styles] || styles.PENDING
  }

  const getStatusText = (status: string) => {
    const texts = {
      PENDING: 'Pendente',
      APPROVED: 'Aprovado',
      REJECTED: 'Rejeitado',
      CANCELLED: 'Cancelado'
    }
    return texts[status as keyof typeof texts] || 'Pendente'
  }

  const getTypeText = (type: string) => {
    const texts = {
      vacation: 'Férias',
      sell: 'Venda de Férias',
      advance: 'Adiantamento'
    }
    return texts[type as keyof typeof texts] || 'Férias'
  }

  if (isLoading) {
    return (
      <div className="space-y-8">
        {/* Breadcrumb Skeleton */}
        <nav className="text-sm text-brand-gray font-body mb-2">
          <span className="hover:text-brand-primary transition-colors cursor-pointer">Dashboard</span>
          <span className="mx-2">›</span>
          <span className="hover:text-brand-primary transition-colors cursor-pointer">Férias</span>
          <span className="mx-2">›</span>
          <span className="text-white font-medium">Gestão de Férias</span>
        </nav>


        {/* My Vacation Balance Card Skeleton */}
        <div className="card bg-gradient-to-br from-brand-primary/10 to-brand-primary/5 border-brand-primary/20 animate-pulse">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-brand-dark-lighter rounded-monumenta flex-shrink-0" />
              <div>
                <div className="h-6 bg-brand-dark-lighter rounded w-32 mb-2" />
                <div className="h-4 bg-brand-dark-lighter rounded w-48" />
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 flex-1 lg:ml-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="text-center lg:text-left">
                  <div className="h-3 bg-brand-dark-lighter rounded w-20 mb-2 mx-auto lg:mx-0" />
                  <div className="h-8 bg-brand-dark-lighter rounded w-12 mx-auto lg:mx-0" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Stats Cards Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="card animate-pulse p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="h-4 bg-brand-dark-lighter rounded w-24 mb-3" />
                  <div className="h-8 bg-brand-dark-lighter rounded w-16" />
                </div>
                <div className="w-12 h-12 bg-brand-dark-lighter rounded-monumenta" />
              </div>
            </div>
          ))}
        </div>

        {/* Tabs Skeleton */}
        <div className="bg-brand-dark-light/80 backdrop-blur-sm rounded-monumenta p-1 shadow-monumenta border border-gray-800/50 animate-pulse">
          <div className="flex space-x-1">
            <div className="flex-1 h-12 bg-brand-dark-lighter rounded-monumenta" />
          </div>
        </div>

        {/* Employee List Skeleton */}
        <div className="card-hover animate-pulse">
          <div className="p-4 sm:p-6 border-b border-brand-gray border-opacity-20">
            <div className="h-6 bg-brand-dark-lighter rounded w-64 mb-2" />
            <div className="h-4 bg-brand-dark-lighter rounded w-48" />
          </div>
          <div className="divide-y divide-brand-gray divide-opacity-20">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                  <div className="flex items-center space-x-4 flex-1">
                    <div className="w-12 h-12 bg-brand-dark-lighter rounded-full flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="h-5 bg-brand-dark-lighter rounded w-40 mb-2" />
                      <div className="h-4 bg-brand-dark-lighter rounded w-56 mb-1" />
                      <div className="h-3 bg-brand-dark-lighter rounded w-32" />
                    </div>
                  </div>
                  <div className="text-center sm:text-right flex-shrink-0">
                    <div className="h-8 bg-brand-dark-lighter rounded w-12 mx-auto sm:ml-auto sm:mr-0 mb-1" />
                    <div className="h-3 bg-brand-dark-lighter rounded w-24 mx-auto sm:ml-auto sm:mr-0" />
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {[...Array(4)].map((_, j) => (
                    <div key={j}>
                      <div className="h-3 bg-brand-dark-lighter rounded w-20 mb-1" />
                      <div className="h-4 bg-brand-dark-lighter rounded w-16" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-8">

        {/* Error State */}
        <div className="text-center py-12">
          <div className="text-error mb-4">
            <AlertTriangle className="mx-auto h-12 w-12" />
          </div>
          <h3 className="font-display text-lg font-medium text-white mb-2">
            Erro ao carregar dados
          </h3>
          <p className="text-brand-gray max-w-md mx-auto font-body mb-4">
            {error}
          </p>
          <button
            onClick={() => loadVacationData()}
            className="btn-primary"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="space-y-8">
        {/* Breadcrumb Navigation */}
        <nav className="text-sm text-brand-gray font-body mb-2">
        <span className="hover:text-brand-primary transition-colors cursor-pointer">Dashboard</span>
        <span className="mx-2">›</span>
        <span className="hover:text-brand-primary transition-colors cursor-pointer">Férias</span>
        <span className="mx-2">›</span>
        <span className="text-white font-medium">Gestão de Férias</span>
      </nav>

        {/* HIDDEN: Nova Solicitação button temporarily disabled */}
        {/* <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-3">
            <a
              href="/vacation/request"
              className="btn-primary inline-flex items-center space-x-2"
            >
              <Plus size={16} />
              <span>Nova Solicitação</span>
            </a>
          </div> */}

        {/* My Vacation Balance Card */}
        {vacationBalance && (
          <div className="card bg-gradient-to-br from-brand-primary/10 to-brand-primary/5 border-brand-primary/20">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-brand-primary/20 rounded-monumenta flex items-center justify-center flex-shrink-0">
                  <Calendar className="text-brand-primary" size={32} />
                </div>
                <div>
                  <h3 className="font-display text-xl font-bold text-white mb-1">Minhas Férias</h3>
                  <p className="text-brand-gray text-sm">Seu saldo e informações de férias</p>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 flex-1 lg:ml-8">
                <div className="text-center lg:text-left">
                  <p className="text-sm text-brand-gray mb-1">Dias Disponíveis</p>
                  <p className="text-3xl font-display font-bold text-brand-primary">
                    {vacationBalance.daysAvailable || 0}
                  </p>
                </div>
                <div className="text-center lg:text-left">
                  <p className="text-sm text-brand-gray mb-1">Período</p>
                  <p className="text-sm font-medium text-white">
                    {vacationBalance.periodYear || '--'}
                  </p>
                </div>
                <div className="text-center lg:text-left">
                  <p className="text-sm text-brand-gray mb-1">Início Aquisição</p>
                  <p className="text-sm font-medium text-white">
                    {formatDateBR(vacationBalance.acquisitionStartDate)}
                  </p>
                </div>
                <div className="text-center lg:text-left">
                  <p className="text-sm text-brand-gray mb-1">Fim Aquisição</p>
                  <p className="text-sm font-medium text-white">
                    {formatDateBR(vacationBalance.acquisitionEndDate)}
                  </p>
                </div>
              </div>

              {/* HIDDEN: Solicitar Férias button temporarily disabled */}
              {/* <div className="flex justify-center lg:justify-end">
                <a
                  href="/vacation/request"
                  className="btn-primary inline-flex items-center"
                >
                  <Plus size={16} className="mr-2" />
                  Solicitar Férias
                </a>
              </div> */}
            </div>

            {vacationBalance.isExpiringSoon && (
              <div className="mt-4 p-3 bg-error/10 rounded border border-error/30">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="text-error flex-shrink-0" size={16} />
                  <p className="text-sm text-error">
                    Atenção: Seu saldo de férias expira em {formatDateBR(vacationBalance.expirationDate)}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Vacation Alerts */}
        {vacationAlerts.length > 0 && (
          <div className="space-y-4">
            <h2 className="font-display text-xl font-semibold text-white flex items-center gap-2">
              <AlertTriangle className="text-error" size={20} />
              Alertas de Férias ({vacationAlerts.length})
            </h2>
            <div className="space-y-3">
              {vacationAlerts.slice(0, 5).map((alert) => (
                <div
                  key={alert.id}
                  className={`rounded-monumenta p-4 ${
                    alert.severity === 'critical'
                      ? 'bg-error/10 border border-error/30'
                      : 'bg-warning/10 border border-warning/30'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <AlertTriangle
                          size={20}
                          className={alert.severity === 'critical' ? 'text-error' : 'text-warning'}
                        />
                        <div className="flex-1">
                          <h4 className="font-display font-medium text-white">
                            <SensitiveText type="name">{alert.employeeName}</SensitiveText>
                          </h4>
                          {alert.actionRequired && (
                            <span className="status-inactive text-xs mt-1 inline-block">
                              AÇÃO REQUERIDA
                            </span>
                          )}
                        </div>
                      </div>
                      <p className={`text-sm font-body ml-8 ${alert.severity === 'critical' ? 'text-error' : 'text-warning'}`}>
                        {alert.message}
                      </p>
                      <div className="flex items-center gap-4 mt-3 ml-8 text-xs text-brand-gray font-body">
                        <span>Saldo atual: {alert.currentBalance} dias</span>
                        {alert.daysUntilAnniversary && (
                          <span>Aniversário em: {alert.daysUntilAnniversary} dias</span>
                        )}
                      </div>
                    </div>
                    {/* HIDDEN: Action buttons temporarily disabled */}
                    {/* <div className="flex flex-col gap-2 ml-4">
                      <button className="btn-primary btn-sm">
                        Agendar Férias
                      </button>
                      {alert.type === 'critical_balance' && (
                        <button className="btn-secondary btn-sm">
                          Notificar RH
                        </button>
                      )}
                    </div> */}
                  </div>
                </div>
              ))}
              {vacationAlerts.length > 5 && (
                <div className="text-center">
                  <button className="text-brand-primary hover:text-white transition-colors duration-300 text-sm font-display">
                    Ver todos os {vacationAlerts.length} alertas
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
          {/* HIDDEN: Solicitações stats temporarily disabled
          <MetricCard
            title="Solicitações"
            value={stats.totalRequests}
            icon={FileText}
            trend="neutral"
            tooltip="Total de todas as solicitações de férias registradas no sistema, independente do status."
          />
          <MetricCard
            title="Pendentes"
            value={stats.pendingRequests}
            icon={Clock}
            trend="neutral"
            tooltip="Número de solicitações aguardando aprovação. Calculado contando apenas solicitações com status 'PENDING'."
          />
          <MetricCard
            title="Aprovadas"
            value={stats.approvedRequests}
            icon={CheckCircle}
            trend="neutral"
            tooltip="Número de solicitações que foram aprovadas. Calculado contando apenas solicitações com status 'APPROVED'."
          />
          <MetricCard
            title="Total de Dias"
            value={stats.totalDaysRequested}
            icon={Calendar}
            trend="neutral"
            tooltip="Soma total de dias de férias solicitados em todas as requisições, independente do status de aprovação."
          />
          */}
          <MetricCard
            title="Alertas Críticos"
            value={stats.criticalAlerts}
            icon={AlertTriangle}
            trend="neutral"
            tooltip="Número de alertas de severidade crítica relacionados a férias que requerem atenção imediata."
          />
          <MetricCard
            title="Avisos"
            value={stats.warningAlerts}
            icon={AlertTriangle}
            trend="neutral"
            tooltip="Número de avisos de severidade warning relacionados a férias que devem ser revisados."
          />
        </div>

        {/* Tabs */}
        <div className="bg-brand-dark-light/80 backdrop-blur-sm rounded-monumenta p-1 shadow-monumenta border border-gray-800/50">
          <div className="flex space-x-1 overflow-x-auto">
            {[
              // HIDDEN: Solicitações tab temporarily disabled
              // { id: 'requests', label: 'Solicitações', icon: FileText },
              { id: 'employees', label: 'Saldo por Colaborador', icon: Users },
              // Hidden tabs: calculator and reports
              // { id: 'calculator', label: 'Calculadora CLT', icon: Calculator },
              // { id: 'reports', label: 'Relatórios', icon: TrendingUp }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setSelectedTab(tab.id as any)}
                className={`px-4 py-3 rounded-monumenta font-display font-semibold transition-all duration-300 flex items-center gap-2 whitespace-nowrap flex-1 justify-center ${
                  selectedTab === tab.id
                    ? 'bg-brand-primary text-brand-dark shadow-monumenta-brand'
                    : 'text-gray-300 hover:text-white hover:bg-brand-dark-lighter'
                }`}
              >
                <tab.icon size={16} />
                <span className="hidden lg:inline">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* HIDDEN: Requests Tab temporarily disabled */}
        {/* {selectedTab === 'requests' && (
          <div className="card-hover animate-fade-in-up">
            <div className="p-4 sm:p-6 border-b border-brand-gray border-opacity-20">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-3 sm:space-y-0">
                <h2 className="font-display text-lg sm:text-xl font-semibold text-white">Solicitações de Férias</h2>
                <div className="flex items-center space-x-3">
                  <button className="btn-ghost btn-sm">
                    <Filter size={16} />
                    <span>Filtrar</span>
                  </button>
                  <button className="btn-ghost btn-sm">
                    <Download size={16} />
                    <span className="hidden sm:inline">Exportar</span>
                  </button>
                </div>
              </div>
            </div>

            {realRequests.length === 0 ? (
              <EmptyState
                icon={FileText}
                title="Nenhuma solicitação de férias"
                description="Não há solicitações de férias registradas no momento. Quando houver novas solicitações, elas aparecerão aqui."
              />
            ) : (
              <div className="divide-y divide-brand-gray divide-opacity-20">
                {realRequests.map((request) => (
                  <div key={request.id} className="p-4 sm:p-6 hover:bg-brand-dark hover:bg-opacity-50 transition-all duration-300">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                      <div className="flex-1">
                        <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                          <div>
                            <h3 className="font-display font-semibold text-white">
                              <SensitiveText type="name">{request.employeeName}</SensitiveText>
                            </h3>
                            <p className="text-sm text-brand-gray">{request.department}</p>
                          </div>
                          <div className="text-sm">
                            <span className={getStatusBadge(request.status)}>
                              {getStatusText(request.status)}
                            </span>
                          </div>
                        </div>

                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 text-sm">
                          <div>
                            <p className="text-brand-gray">Tipo:</p>
                            <p className="font-medium text-white">{getTypeText(request.type)}</p>
                          </div>
                          <div>
                            <p className="text-brand-gray">Período:</p>
                            <p className="font-medium text-white">
                              {formatDateBR(request.startDate)} - {formatDateBR(request.endDate)}
                            </p>
                          </div>
                          <div>
                            <p className="text-brand-gray">Dias:</p>
                            <p className="font-medium text-white">{request.days} dias</p>
                          </div>
                          <div>
                            <p className="text-brand-gray">Solicitado em:</p>
                            <p className="font-medium text-white">{formatDateBR(request.requestDate)}</p>
                          </div>
                        </div>

                        {request.notes && (
                          <div className="mt-3 p-3 bg-brand-dark bg-opacity-50 rounded">
                            <p className="text-sm text-brand-gray">{request.notes}</p>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-row sm:flex-col lg:flex-row items-start sm:items-end lg:items-center space-x-2 sm:space-x-0 sm:space-y-2 lg:space-y-0 lg:space-x-2">
                        {request.status === 'PENDING' && (
                          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                            <button className="btn-primary btn-sm">
                              Aprovar
                            </button>
                            <button className="btn-danger btn-sm">
                              Rejeitar
                            </button>
                          </div>
                        )}
                        <button className="btn-icon">
                          <FileText size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )} */}

        {/* Employees Tab */}
        {selectedTab === 'employees' && (
          <div className="card-hover animate-fade-in-up">
            <div className="p-4 sm:p-6 border-b border-brand-gray border-opacity-20">
              <h2 className="font-display text-lg sm:text-xl font-semibold text-white">Saldo de Férias por Colaborador</h2>
              <p className="text-brand-gray mt-1 text-sm">Cálculos automáticos baseados na CLT</p>
            </div>
            
            <div className="divide-y divide-brand-gray divide-opacity-20">
              {realEmployees.map((employee) => (
                <div key={employee.id} className="p-4 sm:p-6 hover:bg-brand-dark hover:bg-opacity-50 transition-all duration-300 cursor-pointer" onClick={() => setSelectedEmployee(employee)}>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between space-y-4 sm:space-y-0">
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 bg-brand-primary rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-lg font-bold text-brand-dark">
                              {employee.name.charAt(0)}
                            </span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="font-display font-semibold text-white truncate">
                              <SensitiveText type="name">{employee.name}</SensitiveText>
                            </h3>
                            <p className="text-sm text-brand-gray truncate">{employee.position} • {employee.department}</p>
                            <p className="text-xs text-brand-gray opacity-75">
                              Admissão: {formatDateBR(employee.hireDate)}
                            </p>
                          </div>
                        </div>
                        <div className="text-center sm:text-right flex-shrink-0">
                          <p className="font-display text-2xl font-bold text-brand-primary">{employee.vacationBalance.daysAvailable}</p>
                          <p className="text-sm text-brand-gray">dias disponíveis</p>
                        </div>
                      </div>

                      <div className="mt-4 grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 text-sm">
                        <div>
                          <p className="text-brand-gray text-xs">Dias Adquiridos:</p>
                          <p className="font-medium text-brand-primary">{employee.vacationBalance.daysEarned} dias</p>
                        </div>
                        <div>
                          <p className="text-brand-gray text-xs">Dias Usados:</p>
                          <p className="font-medium text-success">{employee.vacationBalance.daysUsed} dias</p>
                        </div>
                        <div>
                          <p className="text-brand-gray text-xs">Dias Agendados:</p>
                          <p className="font-medium text-warning">{employee.vacationBalance.daysScheduled} dias</p>
                        </div>
                        <div>
                          <p className="text-brand-gray text-xs">Período Aquisitivo:</p>
                          <p className="font-medium text-white">{employee.vacationBalance.periodYear}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Calculator Tab */}
        {selectedTab === 'calculator' && (
          <div className="card-hover animate-fade-in-up">
            <div className="text-center py-12">
              <Calculator className="mx-auto h-16 w-16 text-brand-primary animate-pulse" />
              <h3 className="font-display mt-4 text-xl font-semibold text-white">Calculadora CLT</h3>
              <p className="mt-2 text-brand-gray max-w-md mx-auto font-body">
                Calcule férias, 13º salário, rescisão e outros direitos trabalhistas
              </p>
              <div className="mt-8">
                <button className="btn-primary">
                  <Calculator size={16} />
                  Abrir Calculadora
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reports Tab */}
        {selectedTab === 'reports' && (
          <div className="card-hover animate-fade-in-up">
            <div className="text-center py-12">
              <TrendingUp className="mx-auto h-16 w-16 text-brand-primary animate-pulse" />
              <h3 className="font-display mt-4 text-xl font-semibold text-white">Relatórios de Férias</h3>
              <p className="mt-2 text-brand-gray-light max-w-md mx-auto font-body">
                Relatórios detalhados para controle e compliance trabalhista
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Employee Detail Modal */}
      {selectedEmployee && (
        <div className="modal-overlay">
          <div className="modal-content max-w-4xl max-h-[90vh] overflow-y-auto animate-scale-in">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="font-display text-xl sm:text-2xl font-bold text-white">
                  Detalhes de Férias - <SensitiveText type="name">{selectedEmployee.name}</SensitiveText>
                </h2>
                <button
                  onClick={() => setSelectedEmployee(null)}
                  className="btn-icon"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-6">
                {/* Summary */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-brand-primary bg-opacity-10 p-4 rounded border border-brand-primary border-opacity-20">
                    <h3 className="font-display font-semibold text-brand-primary text-sm sm:text-base">Dias Disponíveis</h3>
                    <p className="font-display text-xl sm:text-2xl font-bold text-brand-primary">{selectedEmployee.vacationBalance.daysAvailable}</p>
                  </div>
                  <div className="bg-brand-blue bg-opacity-10 p-4 rounded border border-brand-blue border-opacity-20">
                    <h3 className="font-display font-semibold text-brand-blue text-sm sm:text-base">Dias Adquiridos</h3>
                    <p className="font-display text-xl sm:text-2xl font-bold text-brand-blue">{selectedEmployee.vacationBalance.daysEarned}</p>
                  </div>
                  <div className="bg-brand-gray bg-opacity-10 p-4 rounded border border-brand-gray border-opacity-20">
                    <h3 className="font-display font-semibold text-brand-gray text-sm sm:text-base">Dias Usados</h3>
                    <p className="font-display text-xl sm:text-2xl font-bold text-white">{selectedEmployee.vacationBalance.daysUsed}</p>
                  </div>
                </div>

                {/* Balance Details */}
                <div>
                  <h3 className="font-display text-base sm:text-lg font-semibold text-white mb-4">Detalhes do Período Aquisitivo</h3>
                  <div className="border border-brand-gray border-opacity-20 rounded p-4 bg-brand-dark bg-opacity-50">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-brand-gray mb-1">Período Aquisitivo:</p>
                        <p className="font-medium text-white">{selectedEmployee.vacationBalance.periodYear}</p>
                      </div>
                      <div>
                        <p className="text-sm text-brand-gray mb-1">Dias Agendados:</p>
                        <p className="font-medium text-warning">{selectedEmployee.vacationBalance.daysScheduled} dias</p>
                      </div>
                      <div>
                        <p className="text-sm text-brand-gray mb-1">Início Aquisição:</p>
                        <p className="font-medium text-white">
                          {formatDateBR(selectedEmployee.vacationBalance.acquisitionStartDate)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-brand-gray mb-1">Fim Aquisição:</p>
                        <p className="font-medium text-white">
                          {formatDateBR(selectedEmployee.vacationBalance.acquisitionEndDate)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-brand-gray mb-1">Data de Expiração:</p>
                        <p className={`font-medium ${selectedEmployee.vacationBalance.isExpiringSoon ? 'text-error' : 'text-white'}`}>
                          {new Date(selectedEmployee.vacationBalance.expirationDate).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-brand-gray mb-1">Dias em Risco:</p>
                        <p className={`font-medium ${selectedEmployee.vacationBalance.daysAtRisk > 0 ? 'text-error' : 'text-success'}`}>
                          {selectedEmployee.vacationBalance.daysAtRisk} dias
                        </p>
                      </div>
                    </div>
                    {selectedEmployee.vacationBalance.canSellThird && (
                      <div className="mt-4 p-3 bg-brand-blue bg-opacity-10 rounded border border-brand-blue border-opacity-20">
                        <p className="text-sm text-brand-blue">
                          ✓ Este colaborador pode vender até 1/3 das férias ({Math.floor((selectedEmployee.vacationBalance.daysEarned || selectedEmployee.vacationBalance.daysAvailable || 30) / 3)} dias)
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default function VacationPage() {
  return (
    <AuthProvider>
      <ProtectedRoute>
        <VacationContent />
      </ProtectedRoute>
    </AuthProvider>
  )
} 